import "highcharts/modules/drilldown";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState } from "react";

interface ChartDataItem {
  Name: string;
  Value: number;
  Total: number;
}

interface SocialMatchBarChartViewProps {
  data: {
    items: ChartDataItem[];
    drilldown: ChartDataItem[];
  };
}

export const SocialMatchBarChartView = ({
  data,
}: SocialMatchBarChartViewProps) => {
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isMobile = window.innerWidth < 768;
      const uniqueDays = Array.from(
        new Set(data?.drilldown?.map((d) => d.Name))
      );
      const tooManyDays = uniqueDays.length > 15;

      if (isMobile && tooManyDays) {
        setShouldScroll(true);
      }
    }
  }, [data]);

  if (!data || !data.items || !data.drilldown) {
    return <div>No data available</div>;
  }

  const colors = [
    "#FF2A00",
    "#008048",
    "#35B1E8",
    "#FFA51F",
    "#FEBEC8",
    "#FABC3F",
    "#008044",
    "#35B1E8",
  ];

  const mainSeriesData = data.items.map((item, index) => ({
    name: item.Name,
    y: item.Value,
    Total: item.Total,
    color: colors[index % colors.length],
    drilldown: item.Value > 0 ? item.Name : undefined,
  }));

  const drilldownSeries = data.items
    .filter((item) => item.Value > 0)
    .map((item) => {
      const allDays = Array.from({ length: 30 }, (_, i) => i + 1);
      const dayData = allDays.map((day) => {
        const match = data.drilldown.find((d) => d.Name === `${day}`);

        return {
          name: `${day}`,
          y: match?.Value ?? 0,
          Total: match?.Total ?? 0,
        };
      });

      return {
        id: item.Name,
        name: `${item.Name} Daily Breakdown`,
        data: dayData,
      };
    });

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      ...(shouldScroll && {
        scrollablePlotArea: {
          minWidth: 450, // Or dynamic: uniqueDays.length * 40
          scrollPositionX: 0,
        },
      }),
    },
    title: {
      text: "Social Match Overview",
      style: {
        fontSize: "13px",
      },
    },
    xAxis: {
      type: "category",
      labels: {
        step: 1,
        style: {
          fontSize: "0.9rem",
        },
      },
    },
    yAxis: { title: { text: "Total Matches" } },
    legend: { enabled: false },
    plotOptions: {
      series: {
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: any) {
        return `
          <b>Month:</b> ${this.point.name}<br/>
          <b>Booking:</b> ${this.point.y}<br/>
          <b>Revenue:</b> ${this.point.Total}
        `;
      },
    },
    credits: { enabled: false },
    series: [
      {
        name: "Items",
        colorByPoint: true,
        data: mainSeriesData,
        type: "column",
      },
    ],
    ...(data?.drilldown?.length > 1 && {
      drilldown: {
        series: drilldownSeries,
      } as any,
    }),
  };

  return (
    <div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};
